import { useEffect, useState, type ReactNode } from 'react'

export function Timestamp({ value, now = Date.now() }: { value: string | Date | null | undefined; now?: number }) {
  const date = value instanceof Date ? value : value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return <span className="muted">Unknown</span>
  const seconds = Math.max(0, Math.floor((now - date.getTime()) / 1000))
  const relative = seconds < 60 ? 'just now' : seconds < 3600 ? `${Math.floor(seconds / 60)} min ago` : seconds < 86_400 ? `${Math.floor(seconds / 3600)} hr ago` : `${Math.floor(seconds / 86_400)} day${Math.floor(seconds / 86_400) === 1 ? '' : 's'} ago`
  return <time dateTime={date.toISOString()} title={date.toISOString()}>{relative}</time>
}

export function Duration({ seconds }: { seconds: number | null | undefined }) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return <span className="muted">Unknown</span>
  if (seconds < 60) return <span>{Math.floor(seconds)} sec</span>
  if (seconds < 3600) return <span>{Math.floor(seconds / 60)} min</span>
  if (seconds < 86_400) return <span>{Math.floor(seconds / 3600)} hr</span>
  const days = Math.floor(seconds / 86_400)
  return <span>{days} day{days === 1 ? '' : 's'}</span>
}

export function Percent({ value }: { value: number | null | undefined }) {
  return value == null || !Number.isFinite(value) ? <span className="muted">Unknown</span> : <span>{Math.round(value)}%</span>
}

export function Bytes({ value }: { value: number | null | undefined }) {
  if (value == null || !Number.isFinite(value) || value < 0) return <span className="muted">Unknown</span>
  if (value < 1024) return <span>{value} B</span>
  const units = ['KiB', 'MiB', 'GiB', 'TiB']
  let amount = value
  let unit = -1
  while (amount >= 1024 && unit < units.length - 1) { amount /= 1024; unit += 1 }
  return <span>{amount >= 10 ? Math.round(amount) : amount.toFixed(1)} {units[unit]}</span>
}

export function Truncate({ value, max = 32 }: { value: string; max?: number }) {
  return <span title={value}>{value.length > max ? `${value.slice(0, Math.max(1, max - 1))}…` : value}</span>
}

/**
 * A value with a button that copies it.
 *
 * `label` is the button's visible text. `buttonLabel` names the button for
 * assistive technology when the visible text alone would be ambiguous, and
 * `icon` replaces the text with a glyph — which then swaps to `copiedIcon` for
 * a moment, since an icon that stays a check forever reads as a state rather
 * than a confirmation. The live region announces the copy either way.
 */
export function Copyable({ value, label = 'Copy', buttonLabel, icon, copiedIcon, onCopied, display }: { value: string; label?: string; buttonLabel?: string; icon?: ReactNode; copiedIcon?: ReactNode; onCopied?: () => void; display?: ReactNode }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied || !icon) return
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied, icon])
  const copy = () => {
    try {
      const result = navigator.clipboard?.writeText?.(value)
      if (result && typeof result.catch === 'function') void result.catch(() => undefined)
      setCopied(true)
      onCopied?.()
    } catch {
      setCopied(false)
    }
  }
  const content = icon ? (copied ? copiedIcon ?? icon : icon) : copied ? 'Copied' : label
  return <span className="copyable">{display ?? <code>{value}</code>}<button type="button" onClick={copy} aria-label={buttonLabel ?? (icon ? label : undefined)} title={icon ? buttonLabel ?? label : undefined}>{content}</button><span className="sr-only" role="status" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span></span>
}
